import { computed, type ComputedRef } from 'vue';
import { Dayjs } from 'dayjs';

import { useUnits } from './useUnits';
import { i18n } from '../plugins';
import { createSingleton } from '../utils';

export const useRules = createSingleton((): {
    colorRules: Record<'required', (...args: any[]) => (value: string) => boolean | string>;
    dateRules: Record<'required' | 'min' | 'max', (...args: any[]) => (value: Dayjs[] | Dayjs) => boolean | string>;
    dropdownRules: Record<'required' | 'min' | 'max', (...args: any[]) => (value: string[] | number[] | string | number) => boolean | string>;
    numberRules: Record<'required' | 'min' | 'max', (...args: any[]) => (value: number) => boolean | string>;
    richTextRules: Record<'required', (...args: any[]) => (value: string) => boolean | string>;
    textRules: Record<'required' | 'copy' | 'min' | 'max' | 'email' | 'phone' | 'ipAddress' | 'digit' | 'uppercase' | 'lowercase' | 'special' | 'different' | 'url' | 'start' | 'spreadsheet', (...args: any[]) => (value: string) => boolean | string>;
    timeRules: Record<'required' | 'min', (...args: any[]) => (value: number[] | number) => boolean | string>;
} => {
    const { getDuration } = useUnits();

    const t: ComputedRef<(code: string, params?: Record<string, string | number>) => string> = computed(() => (i18n.value as any).global.t);

    const colorRules: Record<string, (...args: any[]) => (value: string) => boolean | string> = {
        required: () => function required(value: string) {
            return !!value && /^#([0-9a-fA-F]{6})$/.test(value) || t.value('shared.utils.rules.common.required');
        }
    };

    const dateRules: Record<string, (...args: any[]) => (value: Dayjs[] | Dayjs) => boolean | string> = {
        required: () => function required(value: Dayjs[] | Dayjs) {
            return (Array.isArray(value) ? value.length > 0 && !value.some(date => isNaN(+date)): !!value && !isNaN(+value)) || t.value('shared.utils.rules.common.required');
        },
        min: (min: Dayjs) => (value: Dayjs[] | Dayjs) => (Array.isArray(value) ? value.every(date => !isNaN(+date) && +date >= +min) : !isNaN(+value) && +value >= +min) || t.value('shared.utils.rules.date.min', { min: min.format('L') }),
        max: (max: Dayjs) => (value: Dayjs[] | Dayjs) => (Array.isArray(value) ? value.every(date => !isNaN(+date) && +date <= +max) : !isNaN(+value) && +value <= +max) || t.value('shared.utils.rules.date.max', { max: max.format('L') })
    };

    const dropdownRules: Record<string, (...args: any[]) => (value: string[] | number[] | string | number) => boolean | string> = {
        required: () => function required(value: string[] | number[] | string | number) {
            return (Array.isArray(value) ? value.length > 0 : typeof value === 'number' ? !isNaN(value) : !!value) || t.value('shared.utils.rules.common.required');
        },
        min: (min: number) => (value: string[] | number[] | string | number) => (!!value && Array.isArray(value) && value.length >= min) || t.value('shared.utils.rules.dropdown.min', { min }),
        max: (max: number) => (value: string[] | number[] | string | number) => (!Array.isArray(value) || value.length <= max) || t.value('shared.utils.rules.dropdown.max', { max })
    };

    const numberRules: Record<string, (...args: any[]) => (value: number) => boolean | string> = {
        required: () => function required(value: number) {
            return typeof value === 'number' && Number.isFinite(value) || t.value('shared.utils.rules.common.required');
        },
        min: (min: number) => (value: number) => value >= min || t.value('shared.utils.rules.number.min', { min }),
        max: (max: number) => (value: number) => value <= max || t.value('shared.utils.rules.number.max', { max })
    };

    const richTextRules: Record<string, (...args: any[]) => (value: string) => boolean | string> = {
        required: () => function required(value: string) {
            return value != null && value.replace(/<[^>]+>/g, '').replace(/\s/g, '').length > 0 || t.value('shared.utils.rules.common.required');
        }
    };

    const textRules: Record<string, (...args: any[]) => (value: string) => boolean | string> = {
        required: () => function required(value: string) {
            return value != null && value.replace(/\s/g, '').length > 0 || t.value('shared.utils.rules.common.required');
        },
        copy: (original: string) => (value: string) => value === original || t.value('shared.utils.rules.text.copy'),
        min: (min: number) => (value: string) => value.length >= min || t.value('shared.utils.rules.text.min', { min }),
        max: (max: number) => (value: string) => value.length <= max || t.value('shared.utils.rules.text.max', { max }),
        email: () => (value: string) => {
			if (!value || value.trim().length === 0) {
			  return t.value('shared.utils.rules.text.email');
			}
			return (
			  (value.length < 254 && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value))
			  || t.value('shared.utils.rules.text.email')
			);
		  },
        phone: () => (value: string) =>{
			if (!value || value.trim().length === 0) {
				return true;
			}
			return /^\+?\d{1,4}(?:[ -]?\d{1,15}){1,5}$/.test(value) || t.value('shared.utils.rules.text.phone');
		  },
        ipAddress: () => (value: string) => {
            if (!value || value.trim().length === 0) {
                return true;
            }
            return /^(\d{1,3}\.){3}\d{1,3}$/.test(value) || t.value('shared.utils.rules.text.ip-address');
        },
        digit: () => (value: string) => /\d/.test(value) || t.value('shared.utils.rules.text.digit'),
        uppercase: () => (value: string) => /[A-Z]/.test(value) || t.value('shared.utils.rules.text.uppercase'),
        lowercase: () => (value: string) => /[a-z]/.test(value) || t.value('shared.utils.rules.text.lowercase'),
        special: () => (value: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(value) || t.value('shared.utils.rules.text.special'),
        different: (original: string) => (value: string) => value != original || t.value('shared.utils.rules.text.different'),
        url: () => (value: string) => value === '' || /^https?:\/\/(www.)?[^\s/$.?#].[^\s]*$/.test(value) || t.value('shared.utils.rules.text.url'),
        start: (start: string) => (value: string) => value === '' || value.startsWith(start) || t.value('shared.utils.rules.text.start', { start }),
        spreadsheet: () => (value: string) => /^(?:'(?:[^']+)'!)?(?:[A-Z]{1,3}[1-9][0-9]*:[A-Z]{1,3}[1-9][0-9]*)$/.test(value) || t.value('shared.utils.rules.text.spreadsheet')
    };

    const timeRules: Record<string, (...args: any[]) => (value: number[] | number) => boolean | string> = {
        required: () => function required(value: number[] | number) {
            return (Array.isArray(value) ? value.length > 0 : typeof value === 'number' && Number.isFinite(value)) || t.value('shared.utils.rules.common.required');
        },
        min: (min: number) => (value: number[] | number) => (Array.isArray(value) ? value.every(time => !isNaN(+time) && +time >= +min) : !isNaN(+value) && +value >= +min) || t.value('shared.utils.rules.time.min', { min: getDuration(min) })
    };

    return {
        colorRules,
        dateRules,
        dropdownRules,
        numberRules,
        richTextRules,
        textRules,
        timeRules
    };
});